import { useEffect, useId, useMemo, useState } from 'react';
import { errorMessage, useLocale } from '../../i18n';
import {
  loadSupportPlayers,
  supportEditPlayer,
  type SupportEdit,
  type SupportPlayer,
  type SupportPlayersResult,
} from '../../lib/api/playerSupport';
import { safeId } from '../ui/labelledControl';

// ═══════════════════════════════════════════════════════════════════
// /sa → «Spillerprofil»: SUPPORT CORRECTS A PLAYER'S IDENTITY (#796).
//
// A player with an account edits their own identity; clubs are refused. This
// is StarPoint support's door, for when the person cannot do it themself:
// find the player, correct name, profile address, phone or gender, and say
// WHY. The server writes the change and a privileged audit row together, and
// refuses without a reason — the field here is required for the person, not
// as the guarantee.
//
// Offered only to a caller holding `member.profile.support_edit` (see
// `SuperadminRoute`'s `admits`): the section is the edit, so there is nothing
// for anybody else here.
// ═══════════════════════════════════════════════════════════════════

const MIN_REASON = 3;

export interface PlayerProfileSupportSectionProps {
  /** Injectable for tests. */
  load?: typeof loadSupportPlayers;
  save?: typeof supportEditPlayer;
}

export function PlayerProfileSupportSection({
  load = loadSupportPlayers,
  save = supportEditPlayer,
}: PlayerProfileSupportSectionProps) {
  const { t } = useLocale();
  const uid = safeId('sa-player-profile', useId());
  const [data, setData] = useState<SupportPlayersResult | null>(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<SupportPlayer | null>(null);
  const [form, setForm] = useState<SupportEdit>({ name: '', email: '', phone: '', gender: '' });
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<
    null | { kind: 'saved'; changed: number } | { kind: 'failed'; message: string }
  >(null);

  useEffect(() => {
    let cancelled = false;
    void load().then((res) => {
      if (!cancelled) setData(res);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const matches = useMemo(() => {
    if (data?.state !== 'ok') return [];
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return data.players
      .filter((p) =>
        String(p.name ?? '')
          .toLowerCase()
          .includes(q),
      )
      .slice(0, 20);
  }, [data, query]);

  const choose = (p: SupportPlayer) => {
    setSelected(p);
    setForm({
      name: p.name ?? '',
      email: p.email ?? '',
      phone: p.phone ?? '',
      gender: p.gender === 'M' || p.gender === 'F' ? p.gender : '',
    });
    setReason('');
    setOutcome(null);
  };

  const submit = async () => {
    if (!selected) return;
    setBusy(true);
    setOutcome(null);
    const res = await save(selected.id, form, reason.trim());
    setBusy(false);
    if (res.state === 'ok') {
      setSelected({ ...selected, ...res.player });
      setOutcome({ kind: 'saved', changed: res.changed.length });
      setReason('');
    } else if (res.state === 'refused') {
      setOutcome({
        kind: 'failed',
        message:
          res.code === 'no_change' ? t('saPlayerProfile.noChange') : errorMessage(t, res.code),
      });
    } else {
      // NOT «nothing changed»: a failure after the commit cannot prove that.
      setOutcome({ kind: 'failed', message: t('saPlayerProfile.uncertain') });
    }
  };

  const field = (key: keyof SupportEdit, label: string, type = 'text') => (
    <div>
      <label htmlFor={`${uid}-${key}`} className="block text-[11px] text-ink-3">
        {label}
      </label>
      <input
        id={`${uid}-${key}`}
        type={type}
        className="input w-full"
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`sa-player-${key}`}
      />
    </div>
  );

  return (
    <section className="card p-5 space-y-4" aria-labelledby={`${uid}-title`}>
      <h2 id={`${uid}-title`} className="font-semibold">
        {t('saPlayerProfile.title')}
      </h2>
      <p className="text-sm text-ink-3">{t('saPlayerProfile.intro')}</p>

      {data === null && <p role="status">{t('saPlayerProfile.loading')}</p>}
      {data?.state === 'no-api' && <p>{t('saPlayerProfile.noApi')}</p>}
      {(data?.state === 'refused' || data?.state === 'error') && (
        <p role="alert" className="text-rose-700">
          {t('saPlayerProfile.loadFailed')}
        </p>
      )}

      {data?.state === 'ok' && (
        <>
          <div>
            <label htmlFor={`${uid}-search`} className="block text-[11px] text-ink-3">
              {t('saPlayerProfile.search')}
            </label>
            <input
              id={`${uid}-search`}
              className="input w-full"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="sa-player-search"
            />
          </div>
          {matches.length > 0 && (
            <ul className="divide-y divide-line" data-testid="sa-player-matches">
              {matches.map((p) => (
                <li key={String(p.id)}>
                  <button
                    type="button"
                    className="w-full text-left py-2 text-sm hover:bg-bg-soft"
                    aria-pressed={selected?.id === p.id}
                    onClick={() => choose(p)}
                  >
                    {p.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {selected && (
        <form
          className="space-y-3 border-t border-line pt-4"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
          data-testid="sa-player-form"
        >
          {field('name', t('saPlayerProfile.name'))}
          {field('email', t('saPlayerProfile.email'), 'email')}
          {field('phone', t('saPlayerProfile.phone'), 'tel')}
          <div>
            <label htmlFor={`${uid}-gender`} className="block text-[11px] text-ink-3">
              {t('saPlayerProfile.gender')}
            </label>
            <select
              id={`${uid}-gender`}
              className="input w-full"
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
            >
              <option value="">{t('saPlayerProfile.genderUnset')}</option>
              <option value="M">{t('saPlayerProfile.genderMale')}</option>
              <option value="F">{t('saPlayerProfile.genderFemale')}</option>
            </select>
          </div>
          <div>
            <label htmlFor={`${uid}-reason`} className="block text-[11px] text-ink-3">
              {t('saPlayerProfile.reason')}
            </label>
            <textarea
              id={`${uid}-reason`}
              className="input w-full"
              rows={2}
              value={reason}
              required
              onChange={(e) => setReason(e.target.value)}
              data-testid="sa-player-reason"
            />
          </div>
          {outcome?.kind === 'failed' && (
            <p role="alert" className="text-sm text-rose-700" data-testid="sa-player-failed">
              {outcome.message}
            </p>
          )}
          {outcome?.kind === 'saved' && (
            <p role="status" className="text-sm text-ink-2" data-testid="sa-player-saved">
              {t('saPlayerProfile.saved', { count: outcome.changed })}
            </p>
          )}
          <button
            type="submit"
            className="btn-primary text-sm"
            disabled={busy || reason.trim().length < MIN_REASON}
            data-testid="sa-player-save"
          >
            {busy ? t('saPlayerProfile.saving') : t('saPlayerProfile.save')}
          </button>
        </form>
      )}
    </section>
  );
}
